import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createRefetchContainer, RelayRefetchProp } from "react-relay";
import { Button, Dialog, DialogActions, DialogContent } from "@material-ui/core";

import { greys, colors } from "@clockwise/web-commons/src/styles/color.styles";

import {
  IOrg,
  ISearchPerson,
  IViewer,
  SortDirection,
  SortType,
  SuggestedOrgInviteReason,
  SuggestedOrgInviteResolution,
  ActivityFilter,
} from "@schema";

import { BillingGroupsMembers } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members";
import { OrgInvite } from "@components/org-invite";

import { H1 } from "@ui";

import { snackBarOpen } from "@actions/snack-bar.actions";
import {
  updateOrgInvite,
  updateSuggestedOrgInvite,
  addAdminForBillingGroup,
  removeAdminForBillingGroup,
  removeManualMembersForBillingGroup,
  addManualMembersForBillingGroup,
  sendBillingGroupRequestEmail,
} from "@mutations";
import { IReduxState } from "@reducers/root.reducer";

import { AugmentedPersonMap, InvitePerson } from "./AdminPanelBillingGroupsMembers.d";
import { getSuggestedInvitesMap } from "./AdminPanelBillingGroupsMembers.util";
import {
  maybeGetBillingGroupsFromOrg,
  getUserRoleForBillingGroup,
  getBillingGroupNameFromId,
  getAdminsFromBillingGroup,
  getBillingGroupForId,
} from "@util/billing-group.util";
import { maybeGetOrgTeamsFromOrg } from "@util/team.util";
import {
  adminPanelBillingGroupsMembersFragments,
  adminPanelBillingGroupsMembersQuery,
} from "./AdminPanelBillingGroupsMembers.gql";
import { logger } from "@clockwise/client-commons/src/util/logger";
import { track, TrackingEvents } from "@src/util/analytics.util";
import {
  useSelectTable,
  BillingGroupActionType,
  BillingGroupAction,
} from "../../hooks/useSelectTable";

import {
  BillingGroupRoleEnum,
  MemberPerson,
} from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/billing-groups-members.type";
import { AdminSelect } from "../../../admin-select";
import { InvitePanelRenderer } from "./BulkInvite";
import { BulkActionSnackbar } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/bulk-action-snackbar/bulk-action-snackbar";
import { BillingGroup } from "@clockwise/schema";
import { getValue } from "@util/errorable.util";

export interface IAdminPanelBillingGroupsMembersCmpt {
  viewer: IViewer;
  org: IOrg;
  relay: RelayRefetchProp;
  billingGroupId?: string;
}

interface IAdminPanelBillingGroupsMembersProps extends IAdminPanelBillingGroupsMembersCmpt {
  snackBarOpen: (message: string) => void;
}

const AdminPanelBillingGroupsMembersCmpt = (props: IAdminPanelBillingGroupsMembersProps) => {
  const { viewer, org, billingGroupId, relay, snackBarOpen } = props;

  const showBulkInvite = true;

  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [recentlyInvitedPersons, setRecentlyInvitedPersons] = useState<AugmentedPersonMap>({});
  const [
    recentlyRequestedInvitePersons,
    setRecentlyRequestedInvitePersons,
  ] = useState<AugmentedPersonMap>({});
  const [sendingInvite, setSendingInvite] = useState("");

  const [memberSearchQuery, setMemberSearchQuery] = useState<string | null>(null);
  const [teamIdFilter, setTeamIdFilter] = useState<string | null>(null);
  const [billingGroupIdFilter, setBillingGroupIdFilter] = useState<string | null>(
    billingGroupId || null,
  );

  const [currentSortType, setCurrentSortType] = useState<SortType>(SortType.FullName);
  const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection>(
    SortDirection.Ascending,
  );

  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  const [reassignAdminDialogOpen, setReassignAdminDialogOpen] = useState<boolean>(false);

  const [adminSelectDialogBillingGroup, setAdminSelectDialogBillingGroup] = useState<string>(
    billingGroupId || "",
  );
  const [adminIdToRemove, setAdminIdToRemove] = useState<string>("");

  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState<boolean>(false);
  const [selectedBulkAction, setSelectedBulkAction] = useState<BillingGroupAction>();

  const [isInitialUnsortedPersonList, setIsInitialUnsortedPersonList] = useState<boolean>(true);

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("invite") && props.billingGroupId) {
      const searchParamPersonId = searchParams.get("invite") as string;
      onAddMember([searchParamPersonId], props.billingGroupId);
    }
  }, []);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const getAugmentedPersons = () => {
    const suggestedInvitesMap = getSuggestedInvitesMap(org.suggestedInvites);

    // sanity check
    if (!org?.suggestedInvites?.edges) {
      return {} as AugmentedPersonMap;
    }

    let persons: ISearchPerson[] = [];
    const personlist = org.orgPersonListPaginatedErrorable;
    if (personlist.__typename !== "OrgPersonListPaginated") {
      return {} as AugmentedPersonMap;
    }
    persons = personlist.searchPersons ?? [];

    const result = persons.reduce((accumulator: AugmentedPersonMap, nextPerson) => {
      const { primaryCalendarId, profile } = nextPerson.person;
      const recentlySent = !!recentlyInvitedPersons[primaryCalendarId];
      const recentlyRequested = !!recentlyRequestedInvitePersons[primaryCalendarId];

      const primaryBillingGroupIdErrorable = getValue(
        nextPerson.person.primaryBillingGroupIdErrorable,
      );

      const maybeBillingGroup = primaryBillingGroupIdErrorable
        ? getBillingGroupForId(org, primaryBillingGroupIdErrorable.primaryBillingGroupId)
        : undefined;
      accumulator[primaryCalendarId] = {
        status: nextPerson.status,
        targetCalendarId: primaryCalendarId,
        givenName: profile ? profile.givenName : primaryCalendarId, // if no user profile, show primaryCalendarId
        familyName: profile ? profile.familyName : "",
        externalImageUrl: profile ? profile.externalImageUrl : "",
        isSuggested: false,
        recentlySent,
        numberOfSharedMeetings: 0,
        numberOfOrganizedMeetings: 0,
        inviteReason: SuggestedOrgInviteReason.AttendSameMeetings,
        totalScore: 0,
        billingGroup: maybeBillingGroup,
        signUpMillis: nextPerson.person.signUpMillis,
        userId: nextPerson.person.userId,
        personId: nextPerson.person.personId,
        role: maybeBillingGroup
          ? getUserRoleForBillingGroup(maybeBillingGroup, nextPerson.person)
          : BillingGroupRoleEnum.None,
        isYou: nextPerson.person.isYou,
        recentlyRequested,
      };

      // handle suggested invites
      const suggestedInvite = suggestedInvitesMap[primaryCalendarId];
      if (suggestedInvite) {
        accumulator[primaryCalendarId].isSuggested = true;
        accumulator[
          primaryCalendarId
        ].numberOfSharedMeetings = suggestedInvite.numberOfSharedMeetings!;
        accumulator[
          primaryCalendarId
        ].numberOfOrganizedMeetings = suggestedInvite.numberOfOrganizedMeetings!;
        accumulator[primaryCalendarId].totalScore = suggestedInvite.totalScore ?? 0;
        accumulator[primaryCalendarId].inviteReason = suggestedInvite.inviteReason;
      }

      return accumulator;
    }, {});

    // include any recently sent persons
    if (!memberSearchQuery) {
      Object.keys(recentlyInvitedPersons).forEach((cId: string) => {
        result[cId] = recentlyInvitedPersons[cId];
      });
    }

    return result;
  };

  const getCurrentPersonsList = (augmentedPersons: AugmentedPersonMap) => {
    const augmentedPersonsKeys = Object.keys(augmentedPersons);
    const alphabetizedPersonList = augmentedPersonsKeys.map((k) => augmentedPersons[k]);
    const youPerson = alphabetizedPersonList.filter((person) => person.isYou);
    if (isInitialUnsortedPersonList && youPerson) {
      const personsExcludingYou = alphabetizedPersonList.filter((person) => !person.isYou);
      return youPerson.concat(...personsExcludingYou);
    } else {
      if (!augmentedPersonsKeys) {
        return [];
      }
      return alphabetizedPersonList;
    }
  };

  const getPersonFromCalId = (calId: string) => {
    return currentPersons.find((p) => p.targetCalendarId === calId);
  };

  const getPreviousPersonId = () => {
    const personList = org?.orgPersonListPaginatedErrorable;
    if (personList.__typename !== "OrgPersonListPaginated") {
      return null;
    }
    return personList.previousPersonId;
  };

  const getNextPersonId = () => {
    const personList = org?.orgPersonListPaginatedErrorable;
    if (personList.__typename !== "OrgPersonListPaginated") {
      return null;
    }
    return personList.nextPersonId;
  };

  const augmentedPersons = getAugmentedPersons();
  const [currentPersons, setCurrentPersons] = useState<InvitePerson[]>(
    getCurrentPersonsList(augmentedPersons),
  );
  const [previousPersonId, setPreviousPersonId] = useState<string | null>(getPreviousPersonId());
  const [nextPersonId, setNextPersonId] = useState<string | null>(getNextPersonId());

  useEffect(() => {
    const newAugmentedPersons = getAugmentedPersons();
    const newPersons = getCurrentPersonsList(newAugmentedPersons);

    setCurrentPersons(newPersons);

    setPreviousPersonId(getPreviousPersonId());
    setNextPersonId(getNextPersonId());
  }, [org]);

  const updateCurrentPersonInviteStatus = (calId: string) => {
    const updatedPersons = currentPersons.map((person) => {
      if (person.targetCalendarId === calId) {
        const updatedPerson = {
          ...person,
          isPending: true,
        };

        return updatedPerson;
      }

      return person;
    });

    setCurrentPersons(updatedPersons);
  };

  const updateCurrentPersonRole = (userId: string, role: BillingGroupRoleEnum) => {
    const updatedPersons = currentPersons.map((person) => {
      if (person.userId === userId) {
        const updatedPerson = {
          ...person,
          role,
        };

        return updatedPerson;
      }

      return person;
    });

    setCurrentPersons(updatedPersons);
  };

  const removePerson = (_personId: string[]) => {
    // const updatedPersons = currentPersons.filter((p) => p.personId !== personId);
    // setCurrentPersons(updatedPersons);
    // refetch
    handleMemberSearch(memberSearchQuery || "");
  };

  const addPersonToBillingGroup = (_personIds: string[], _billingGroupId: string) => {
    // console.log(`add person: ${personId} ${billingGroupId}`);
    // refetch
    handleMemberSearch(memberSearchQuery || "");
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  const updateRecentlyInvitedPersons = (calId: string) => {
    const person = getPersonFromCalId(calId);
    const invitedPersons = { ...recentlyInvitedPersons };
    if (person) {
      person.recentlySent = true;
      invitedPersons[calId] = person;
    }

    setRecentlyInvitedPersons(invitedPersons);
  };

  const onInviteSuccess = (email: string) => {
    setSendingInvite("");
    updateRecentlyInvitedPersons(email);
    updateCurrentPersonInviteStatus(email);
    snackBarOpen("Invitation sent!");
  };

  const onInviteFailure = () => {
    setSendingInvite("");
    snackBarOpen("Invitation failed to send! :(");
  };

  const handleSendInvite = (calId: string) => {
    setSendingInvite(calId);
    const suggested = getPersonFromCalId(calId)?.isSuggested;
    const calendarIds = [calId];
    if (suggested) {
      const resolution = "Accepted" as SuggestedOrgInviteResolution;
      track(TrackingEvents.ADMIN_PANEL.MEMBERS_SEND_SUGGESTED_INVITE, { calendarId: calId });

      updateSuggestedOrgInvite(
        relay.environment,
        { calendarIds, resolution, orgRelayId: org.id },
        () => onInviteSuccess(calId),
        onInviteFailure,
      );
    } else if (!suggested) {
      track(TrackingEvents.ADMIN_PANEL.MEMBERS_SEND_INVITE);
      updateOrgInvite(
        relay.environment,
        { calendarIds, orgRelayId: org.id },
        () => onInviteSuccess(calId),
        onInviteFailure,
      );
    }
  };
  const getActivityFilter = (_billingGroupId: string | null, _teamId: string | null) => {
    if (!!_billingGroupId || !!_teamId) {
      return ActivityFilter.CurrentUser;
    } else {
      return ActivityFilter.CurrentUserOrInvited;
    }
  };

  const handleMemberSearch = (query: string) => {
    const newSearchQuery = query || null;
    setMemberSearchQuery(newSearchQuery);
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_SEARCH);

    relay.refetch(
      {
        orgRelayId: org.id,
        searchQuery: newSearchQuery,
        teamId: teamIdFilter,
        billingGroupId: billingGroupIdFilter,
        activityFilter: getActivityFilter(billingGroupIdFilter, teamIdFilter),
      },
      null,
      (error?: Error) => {
        if (error) {
          logger.error("Error on AdminPanelBillingGroupsMembers search refetch", error);
        }
      },
      {
        force: true,
      },
    );
  };

  const handleMultiFilterSelect = (teamId: string, billingGroupId: string) => {
    const newTeamId = teamId || null;
    const newBillingGroupId = billingGroupId || null;

    setTeamIdFilter(newTeamId);
    setBillingGroupIdFilter(newBillingGroupId);

    if (newTeamId) {
      track(TrackingEvents.ADMIN_PANEL.MEMBERS_MULTIFILTER_FILTER_TEAM);
    }
    if (newBillingGroupId) {
      track(TrackingEvents.ADMIN_PANEL.MEMBERS_MULTIFILTER_FILTER_BILLING_GROUP);
    }

    relay.refetch(
      {
        orgRelayId: org.id,
        searchQuery: memberSearchQuery,
        teamId: newTeamId,
        billingGroupId: newBillingGroupId,
        activityFilter: getActivityFilter(newBillingGroupId, newTeamId),
      },
      null,
      (error?: Error) => {
        if (error) {
          logger.error("Error on AdminPanelBillingGroupsMembers multi-filter refetch", error);
        }
      },
      {
        force: true,
      },
    );
  };

  const sortByDirection = () => {
    const newSortDirection =
      currentSortDirection === SortDirection.Ascending
        ? SortDirection.Descending
        : SortDirection.Ascending;

    track(TrackingEvents.ADMIN_PANEL.MEMBERS_SORT, {
      sortType: currentSortType,
      sortDirection: newSortDirection,
    });

    setCurrentSortDirection(newSortDirection);
    relay.refetch(
      {
        orgRelayId: org.id,
        sortType: currentSortType,
        sortDirection: newSortDirection,
        teamId: teamIdFilter,
        billingGroupId: billingGroupIdFilter,
      },
      null,
      (error?: Error) => {
        if (error) {
          logger.error("Error on AdminPanelBillingGroupsMembers sort by direction refetch", error);
        }
      },
      {
        force: true,
      },
    );
  };

  const sortByType = (newSortType: SortType) => {
    setCurrentSortType(newSortType);
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_SORT, {
      sortType: newSortType,
      sortDirection: currentSortDirection,
    });

    relay.refetch(
      {
        orgRelayId: org.id,
        sortType: newSortType,
        sortDirection: currentSortDirection,
        teamId: teamIdFilter,
        billingGroupId: billingGroupIdFilter,
      },
      null,
      (error?: Error) => {
        if (error) {
          logger.error("Error on AdminPanelBillingGroupsMembers sort by type refetch", error);
        }
      },
      {
        force: true,
      },
    );
  };

  const handleSort = (type: SortType) => {
    setIsInitialUnsortedPersonList(false);
    setMemberSearchQuery(() => null);
    if (type === currentSortType) {
      sortByDirection();
    } else {
      sortByType(type);
    }
  };

  const handlePaginationAction = (pageToken: string | null, fetchNext: boolean) => {
    relay.refetch(
      {
        orgRelayId: org.id,
        sortType: currentSortType,
        sortDirection: currentSortDirection,
        pageToken,
        fetchNext,
        teamId: teamIdFilter,
        billingGroupId: billingGroupIdFilter,
      },
      null,
      (error?: Error) => {
        if (error) {
          logger.error("Error on AdminPanelBillingGroupsMembers pagination refetch", error);
        }
      },
      {
        force: true,
      },
    );
  };

  const handleInviteDialogOpen = (_open: boolean) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_OPEN_INVITE_DIALOG);
    setInviteDialogOpen(true);
  };

  const onReassignNewAdmin = (userId: string, billingGroupId: string) => {
    if (adminIdToRemove.length > 0) {
      addAdminForBillingGroup(
        relay.environment,
        {
          orgRelayId: org.id,
          billingGroupId,
          userId,
        },
        () => onReassignNewAdminSuccess(adminIdToRemove, billingGroupId),
        () => onReassignNewAdminFailure(),
      );
    }
  };

  const onReassignNewAdminSuccess = (userIdToRemove: string, billingGroupId: string) => {
    setReassignAdminDialogOpen(false);
    snackBarOpen("New Admin added sucessfully");
    onRemoveMember([userIdToRemove], billingGroupId);
  };

  const onReassignNewAdminFailure = () => {
    const errorMsg = "Failed to add new admin";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onAdminChangeSuccess = (role: BillingGroupRoleEnum, userId: string) => {
    snackBarOpen("Successfully updated role!");
    updateCurrentPersonRole(userId, role);
    handleMemberSearch(memberSearchQuery || "");
  };

  const onAdminChangeFailure = (_role: BillingGroupRoleEnum) => {
    const errorMsg = "Failed to update role";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onAdminSelectChange = (
    userId: string,
    billingGroupId: string,
    role: BillingGroupRoleEnum,
    personId: string,
    errorMsg?: string,
  ) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_SET_BILLING_GROUP_ROLE, { role });
    if (errorMsg && personId) {
      setAdminIdToRemove(personId);
      setAdminSelectDialogBillingGroup(billingGroupId);
      setReassignAdminDialogOpen(true);
    } else if (role === BillingGroupRoleEnum.Admin) {
      addAdminForBillingGroup(
        relay.environment,
        {
          orgRelayId: org.id,
          billingGroupId,
          userId,
        },
        () => onAdminChangeSuccess(BillingGroupRoleEnum.Admin, userId),
        () => onAdminChangeFailure(BillingGroupRoleEnum.Admin),
      );
    } else {
      removeAdminForBillingGroup(
        relay.environment,
        {
          orgRelayId: org.id,
          billingGroupId,
          userId,
        },
        () => onAdminChangeSuccess(BillingGroupRoleEnum.Member, userId),
        () => onAdminChangeFailure(BillingGroupRoleEnum.Member),
      );
    }
  };

  const onRemoveMemberSuccess = (personIds: string[]) => {
    setSelectedMembers([]);
    removePerson(personIds);
    const message =
      personIds.length > 1 ? "Successfully removed members!" : "Successfully removed member!";
    snackBarOpen(message);
  };

  const onRemoveMemberFailure = () => {
    const errorMsg = "Failed to remove member";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onRemoveMember = (personIds: string[], billingGroupId: string, errorMsg?: string) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_REMOVE_FROM_BILLING_GROUP, { billingGroupId });
    if (errorMsg) {
      setAdminIdToRemove(personIds[0]);
      setAdminSelectDialogBillingGroup(billingGroupId);
      setReassignAdminDialogOpen(true);
    } else {
      removeManualMembersForBillingGroup(
        relay.environment,
        {
          orgRelayId: org.id,
          billingGroupId,
          personIds: personIds,
        },
        () => onRemoveMemberSuccess(personIds),
        () => onRemoveMemberFailure(),
      );
    }
  };

  const onAddMemberSuccess = (personIds: string[], billingGroupId: string) => {
    setSelectedMembers([]);
    const message =
      personIds.length > 1 ? "Successfully added members!" : "Successfully added member!";
    snackBarOpen(message);
    addPersonToBillingGroup(personIds, billingGroupId);
  };

  const onAddMemberFailure = () => {
    const errorMsg = "Failed to add member";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onAddMember = (personIds: string[], billingGroupId: string) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_ADD_TO_BILLING_GROUP, { billingGroupId });
    addManualMembersForBillingGroup(
      relay.environment,
      {
        orgRelayId: org.id,
        billingGroupId,
        personIds: personIds,
      },
      () => onAddMemberSuccess(personIds, billingGroupId),
      () => onAddMemberFailure(),
    );
  };

  const updateRecentlyRequestedInvitePersons = (calId: string) => {
    const person = getPersonFromCalId(calId);
    const requestedInvitePersons = { ...recentlyRequestedInvitePersons };
    if (person) {
      person.recentlyRequested = true;
      requestedInvitePersons[calId] = person;
    }

    setRecentlyRequestedInvitePersons(requestedInvitePersons);
  };

  const onRequestToJoinMember = (personId: string, billingGroupId: string, email: string) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_REQUEST_TO_JOIN_BILLING_GROUP, { billingGroupId });
    sendBillingGroupRequestEmail(
      relay.environment,
      {
        orgRelayId: org.id,
        billingGroupId,
        personId,
      },
      () => onRequestToJoinMemberSuccess(email),
      () => onRequestToJoinMemberFailure(),
    );
  };

  const onRequestToJoinMemberSuccess = (email: string) => {
    snackBarOpen("Request sent!");
    updateRecentlyRequestedInvitePersons(email);
  };

  const onRequestToJoinMemberFailure = () => {
    const errorMsg = "Request failed to send";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onCloseBulkInviteAfterInvite = () => {
    relay.refetch(
      {
        orgRelayId: org.id,
        sortType: currentSortType,
        sortDirection: currentSortDirection,
        teamId: teamIdFilter,
        billingGroupId: billingGroupIdFilter,
      },
      null,
      (error?: Error) => {
        if (error) {
          logger.error(
            "Error on AdminPanelBillingGroupsMembers refetch after closing bulk invite",
            error,
          );
        }
      },
      {
        force: true,
      },
    );
    setInviteDialogOpen(false);
  };

  const teamsForOrg = maybeGetOrgTeamsFromOrg(org) || []; // TODO (lsanwick) Remove casting when migrating to `@clockwise/schema`
  const billingGroupsForOrg = [...(maybeGetBillingGroupsFromOrg(org) || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const teamOptions = teamsForOrg.map((t) => ({
    label: t.teamName,
    value: t.teamId,
  }));

  const billingGroupOptions = billingGroupsForOrg.map((bg) => ({
    label: bg.name,
    value: bg.id,
  }));

  // ~-~-~-~-~-~-~-
  // Select table helpers
  // ~-~-~-~-~-~-~-

  const getPersonIdList = (persons: MemberPerson[]) => {
    return persons.map((p: MemberPerson) => p.personId);
  };

  const onSelectBulkAction = (billingGroupId: string, actionType: string) => {
    setSelectedBulkAction({ type: actionType, billingGroupId: billingGroupId });
    setBulkActionDialogOpen(true);
  };

  const onConfirmBulkAction = (billingGroupId: string, actionType: string) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_USE_BULK_MEMBER_ACTION, { billingGroupId });
    setBulkActionDialogOpen(false);
    const personList = getPersonIdList(selectedMembers);
    switch (actionType) {
      case BillingGroupActionType.add:
        onAddMember(personList, billingGroupId);
        break;
      case BillingGroupActionType.remove:
        const containsAdmin = selectedMembers.some(
          (member: MemberPerson) => member.role === BillingGroupRoleEnum.Admin,
        );
        if (containsAdmin) {
          const adminsToRemove = selectedMembers.filter(
            (member: MemberPerson) => member.role === BillingGroupRoleEnum.Admin,
          );

          const bg = getBillingGroupForId(org, billingGroupId);
          const BgAdmins = bg ? getAdminsFromBillingGroup(bg) : [];

          if (!!BgAdmins && adminsToRemove.length >= BgAdmins.length) {
            snackBarOpen(`Cannot remove all admins. Please assign another admin first.`);
            setSelectedMembers([]);
          } else {
            onRemoveMember(personList, billingGroupId);
          }
        } else {
          onRemoveMember(personList, billingGroupId);
        }
        break;
      default:
        snackBarOpen("Could not complete that action");
        break;
    }
  };

  const {
    selectedMembers,
    setSelectedMembers,
    onSelectOneMember,
    onSelectAllMembers,
    isSelectDialogShowing,
    setIsSelectDialogShowing,
    availableActions,
    isSelectAllChecked,
    isSelectTableDisabled,
  } = useSelectTable(currentPersons as MemberPerson[], billingGroupsForOrg);

  const closeBulkActionDialog = (shouldDeleteSelectedMembers: boolean) => {
    setIsSelectDialogShowing(false);
    if (shouldDeleteSelectedMembers) {
      setSelectedMembers([]);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  return (
    <>
      <BillingGroupsMembers
        value={memberSearchQuery ?? ""}
        persons={currentPersons as MemberPerson[]}
        billingGroups={(billingGroupsForOrg as unknown) as BillingGroup[]}
        previousPersonId={previousPersonId}
        nextPersonId={nextPersonId}
        sortDirection={currentSortDirection}
        sortType={currentSortType}
        onSearch={handleMemberSearch}
        onMultiFilterSelect={handleMultiFilterSelect}
        onSort={handleSort}
        sendInvite={handleSendInvite}
        sendingInvite={sendingInvite}
        onPaginationAction={handlePaginationAction}
        teamOptions={teamOptions}
        billingGroupOptions={billingGroupOptions}
        onAdminSelectChange={onAdminSelectChange}
        onRemoveMember={onRemoveMember}
        onAddMember={onAddMember}
        onRequestToJoinMember={onRequestToJoinMember}
        billingGroupIdFilter={billingGroupIdFilter || undefined}
        teamIdFilter={teamIdFilter || undefined}
        onSetInviteDialogOpen={handleInviteDialogOpen}
        selectedMembers={selectedMembers || []}
        onSelectOneMember={onSelectOneMember}
        onSelectAllMembers={onSelectAllMembers}
        isSelectAllChecked={isSelectAllChecked}
        isSelectDisabled={isSelectTableDisabled}
      />
      {isSelectDialogShowing && (
        <BulkActionSnackbar
          label={`${selectedMembers.length} users selected`}
          actionList={availableActions}
          onClose={closeBulkActionDialog}
          onSelect={onSelectBulkAction}
        />
      )}
      <ReassignAdminDialog
        open={reassignAdminDialogOpen}
        onClose={() => setReassignAdminDialogOpen(false)}
        org={org}
        billingGroupName={getBillingGroupNameFromId(org, adminSelectDialogBillingGroup)}
        billingGroupId={adminSelectDialogBillingGroup}
        onReassignNewAdmin={onReassignNewAdmin}
      />
      {showBulkInvite ? (
        <BulkInviteDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          onCloseAfterInvite={onCloseBulkInviteAfterInvite}
          orgRelayId={org.id}
        />
      ) : (
        <InviteDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          viewer={viewer}
          org={org}
        />
      )}
      {bulkActionDialogOpen && !!selectedBulkAction && (
        <ConfirmBulkActionDialog
          open={bulkActionDialogOpen}
          onClose={() => {
            setBulkActionDialogOpen(false);
            setSelectedMembers([]);
          }}
          onConfirm={onConfirmBulkAction}
          persons={selectedMembers}
          action={selectedBulkAction}
          billingGroupName={
            getBillingGroupNameFromId(org, selectedBulkAction?.billingGroupId) || ""
          }
        />
      )}
    </>
  );
};

interface IInviteDialogProps {
  org: IOrg;
  viewer: IViewer;
  open: boolean;
  onClose: () => void;
}

const InviteDialog = ({ viewer, org, open, onClose }: IInviteDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
          Share Clockwise with coworkers
        </H1>
        <div
          style={{
            color: greys.slate,
            fontSize: 14,
            lineHeight: 1.5,
            paddingBottom: 16,
          }}
        >
          When your colleagues use Clockwise, it increases the likelihood of finding a better time
          for your meetings and opens up more Focus Time for everyone.
        </div>
        <OrgInvite viewer={viewer} org={org} showCurrentUser={false} />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface IBulkInviteDialogProps {
  orgRelayId: string;
  open: boolean;
  onClose: () => void;
  onCloseAfterInvite: () => void;
}
const BulkInviteDialog = ({
  open,
  onClose,
  onCloseAfterInvite,
  orgRelayId,
}: IBulkInviteDialogProps) => {
  return (
    <InvitePanelRenderer
      orgRelayId={orgRelayId}
      open={open}
      onClose={onClose}
      onCloseAfterInvite={onCloseAfterInvite}
    />
  );
};

interface IReassignAdminDialogProps {
  open: boolean;
  onClose: () => void;
  org: IOrg;
  billingGroupName?: string;
  billingGroupId: string;
  onReassignNewAdmin: (userId: string, billingGroupId: string) => void;
}

const ReassignAdminDialog = ({
  open,
  onClose,
  billingGroupName,
  org,
  billingGroupId,
  onReassignNewAdmin,
}: IReassignAdminDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>Select a new admin</H1>
        <div
          style={{
            color: greys.slate,
            fontSize: 14,
            lineHeight: 1.5,
            paddingBottom: 16,
          }}
        >
          You’re the only admin of {billingGroupName}, please select a new admin before you leave
          the plan.
        </div>
        <AdminSelect
          onReassignNewAdmin={onReassignNewAdmin}
          billingGroupId={billingGroupId}
          org={org}
          closeAdminSelectDialog={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};
interface IConformBulkActionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (billingGroupId: string, actionType: string) => void;
  persons: MemberPerson[];
  action: BillingGroupAction;
  billingGroupName: string;
}

const ConfirmBulkActionDialog = ({
  open,
  onClose,
  onConfirm,
  persons,
  action,
  billingGroupName,
}: IConformBulkActionDialogProps) => {
  const personEmailList = persons.map((person) => person.targetCalendarId);

  const personString = personEmailList.join(", ");

  const message =
    action.type === BillingGroupActionType.remove
      ? `Are you sure you want to remove ${personString}? They will loose access to all existing paid features.`
      : `Do you want to add ${personString} to ${billingGroupName}?`;

  const upperCaseFirstLetter = (s: string) => {
    const str = s.toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
          {upperCaseFirstLetter(action.type)} users from {billingGroupName}?
        </H1>
        <div
          style={{
            color: greys.slate,
            fontSize: 14,
            lineHeight: 1.5,
            paddingTop: 20,
          }}
        >
          {message}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="text"
          style={{
            color:
              action.type === BillingGroupActionType.add
                ? colors.green.standard
                : colors.red.standard,
          }}
          onClick={() => onConfirm(action.billingGroupId, action.type)}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function mapStateToProps(_state: IReduxState, _ownProps: IAdminPanelBillingGroupsMembersCmpt) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AdminPanelBillingGroupsMembersCmpt);

export const AdminPanelBillingGroupsMembers = createRefetchContainer(
  reduxContainer,
  adminPanelBillingGroupsMembersFragments,
  adminPanelBillingGroupsMembersQuery,
);
